/** @format */

import React, { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import {
    Box,
    Typography,
    CircularProgress,
    Switch,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    TextField,
    MenuItem,
    IconButton,
    Tooltip,
    Divider,
} from "@mui/material";
import {
    AddCircleOutline,
    EditOutlined,
    DeleteOutline,
    Add,
    Remove,
    CampaignOutlined,
} from "@mui/icons-material";

import { AppDispatch } from "../../redux/store/store";
import {
    fetchCampaigns,
    fetchTemplates,
    createCampaign,
    updateCampaign,
    deleteCampaign,
} from "../../redux/actions/collectAction";
import {
    selectActiveBusinessId,
    selectCampaigns,
    selectIsLoadingCampaigns,
    selectCampaignsError,
    selectTemplates,
    selectIsSavingCampaign,
    selectCampaignSaveError,
} from "../../redux/selectors/collectSelector";
import {
    IDunningCampaign,
    IDunningRule,
    DunningRuleTrigger,
    DunningChannel,
} from "../../utility/types/collectTypes";
import { colors } from "../../utility/types/constant";
import { useToast } from "../../providers/ToastProvider";
import {
    CollectPageWrapper,
    CollectSectionCard,
    CollectEmptyState,
    CollectEmptyTitle,
    CollectEmptyHint,
    CollectPrimaryButton,
    CollectGhostButton,
    CollectInfoBanner,
    FlexBetween,
} from "./styles";

// ─────────────────────────────────────────────────────────────────────────
// CollectCampaigns — list of dunning campaigns + dialog editor for rules.
// A "campaign" is a set of rules that fire at specific offsets relative
// to a customer's invoice due date.
// ─────────────────────────────────────────────────────────────────────────

const TRIGGER_LABELS: Record<DunningRuleTrigger, string> = {
    before_due: "days before due",
    due_today: "on due date",
    after_due: "days after due",
};

const CHANNEL_LABELS: Record<DunningChannel, string> = {
    email: "Email",
    sms: "SMS",
    both: "Email + SMS",
};

function emptyRule(): IDunningRule {
    return {
        name: "New rule",
        trigger: "after_due",
        offsetDays: 3,
        channel: "email",
        templateId: "",
        isActive: true,
    };
}

function emptyCampaign(): IDunningCampaign {
    return {
        _id: "",
        businessId: "",
        name: "",
        description: "",
        isDefault: false,
        isActive: true,
        rules: [emptyRule()],
    };
}

const CollectCampaigns: React.FC = () => {
    const dispatch = useDispatch<AppDispatch>();
    const toast = useToast();
    const businessId = useSelector(selectActiveBusinessId);
    const campaigns = useSelector(selectCampaigns);
    const isLoading = useSelector(selectIsLoadingCampaigns);
    const error = useSelector(selectCampaignsError);
    const templates = useSelector(selectTemplates);
    const isSaving = useSelector(selectIsSavingCampaign);
    const saveError = useSelector(selectCampaignSaveError);

    const [dialogOpen, setDialogOpen] = useState(false);
    const [draft, setDraft] = useState<IDunningCampaign>(emptyCampaign());

    useEffect(() => {
        if (!businessId) return;
        dispatch(fetchCampaigns(undefined));
        if (templates.length === 0) dispatch(fetchTemplates({}));
    }, [dispatch, businessId, templates.length]);

    const openCreate = () => {
        setDraft({
            ...emptyCampaign(),
            rules: [
                { ...emptyRule(), templateId: templates[0]?._id || "" },
            ],
        });
        setDialogOpen(true);
    };

    const openEdit = (c: IDunningCampaign) => {
        // Clone to avoid mutating selector data
        setDraft(JSON.parse(JSON.stringify(c)));
        setDialogOpen(true);
    };

    const onSave = async () => {
        if (!draft.name.trim()) return;
        const payload = {
            name: draft.name.trim(),
            description: draft.description?.trim() || undefined,
            isDefault: draft.isDefault,
            isActive: draft.isActive,
            rules: draft.rules.map((r) => ({
                name: r.name,
                trigger: r.trigger,
                offsetDays: Number(r.offsetDays) || 0,
                channel: r.channel,
                templateId: r.templateId,
                minBalance: r.minBalance,
                isActive: r.isActive,
            })),
        };
        const action = draft._id
            ? updateCampaign({ campaignId: draft._id, ...payload })
            : createCampaign(payload);
        const result = await dispatch(action as any);
        if ((result as any).meta?.requestStatus === "fulfilled") {
            setDialogOpen(false);
            toast.success(draft._id ? "Campaign updated." : "Campaign created.");
        } else {
            toast.error(
                (result as any).payload || "Couldn't save campaign. Try again.",
            );
        }
    };

    const onDelete = async (c: IDunningCampaign) => {
        if (!window.confirm(`Delete campaign "${c.name}"? This can't be undone.`)) {
            return;
        }
        const result: any = await dispatch(deleteCampaign({ campaignId: c._id }));
        if (result?.meta?.requestStatus === "fulfilled") {
            toast.success("Campaign deleted.");
        } else {
            toast.error("Couldn't delete campaign.");
        }
    };

    const toggleActive = async (c: IDunningCampaign) => {
        await dispatch(
            updateCampaign({
                campaignId: c._id,
                name: c.name,
                description: c.description,
                isDefault: c.isDefault,
                isActive: !c.isActive,
                rules: c.rules,
            }),
        );
    };

    const updateRule = (idx: number, patch: Partial<IDunningRule>) => {
        setDraft((d) => ({
            ...d,
            rules: d.rules.map((r, i) => (i === idx ? { ...r, ...patch } : r)),
        }));
    };

    const addRule = () => {
        setDraft((d) => ({
            ...d,
            rules: [
                ...d.rules,
                { ...emptyRule(), templateId: templates[0]?._id || "" },
            ],
        }));
    };

    const removeRule = (idx: number) => {
        setDraft((d) => ({
            ...d,
            rules: d.rules.filter((_, i) => i !== idx),
        }));
    };

    return (
        <CollectPageWrapper>
            <Box
                sx={{
                    maxWidth: "1200px",
                    margin: "0 auto",
                    padding: { xs: "20px 12px", md: "32px 24px" },
                }}
            >
                <FlexBetween style={{ marginBottom: 8 }}>
                    <Box>
                        <Typography
                            sx={{
                                fontFamily: "'Montserrat', sans-serif",
                                fontWeight: 800,
                                fontSize: "clamp(1.4rem, 2.4vw, 1.8rem)",
                                color: colors.primary,
                            }}
                        >
                            Dunning campaigns
                        </Typography>
                        <Typography
                            sx={{
                                fontFamily: "'Montserrat', sans-serif",
                                color: "#6b7280",
                                fontSize: "0.9rem",
                                mt: 0.5,
                                maxWidth: 720,
                            }}
                        >
                            Set up automatic reminders that fire before, on, and after an
                            invoice's due date. Each rule picks a template and a channel.
                        </Typography>
                    </Box>
                    <CollectPrimaryButton
                        startIcon={<AddCircleOutline fontSize="small" />}
                        onClick={openCreate}
                        disabled={!businessId}
                    >
                        New campaign
                    </CollectPrimaryButton>
                </FlexBetween>

                {error && (
                    <CollectInfoBanner tone="warn">
                        <span>{error}</span>
                    </CollectInfoBanner>
                )}

                {isLoading && campaigns.length === 0 ? (
                    <Box sx={{ display: "flex", justifyContent: "center", py: 6 }}>
                        <CircularProgress sx={{ color: colors.mainSecondary }} />
                    </Box>
                ) : campaigns.length === 0 ? (
                    <CollectSectionCard>
                        <CollectEmptyState>
                            <CampaignOutlined
                                sx={{ fontSize: 36, color: "#9ca3af", mb: 1 }}
                            />
                            <CollectEmptyTitle>No campaigns yet</CollectEmptyTitle>
                            <CollectEmptyHint>
                                Create your first campaign to start sending automatic
                                reminders for overdue invoices.
                            </CollectEmptyHint>
                            <Box sx={{ mt: 2 }}>
                                <CollectPrimaryButton onClick={openCreate}>
                                    Create campaign
                                </CollectPrimaryButton>
                            </Box>
                        </CollectEmptyState>
                    </CollectSectionCard>
                ) : (
                    campaigns.map((c) => (
                        <CollectSectionCard key={c._id}>
                            <FlexBetween>
                                <Box sx={{ flex: 1 }}>
                                    <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                                        <Typography
                                            sx={{
                                                fontFamily: "'Montserrat', sans-serif",
                                                fontWeight: 700,
                                                color: colors.primary,
                                                fontSize: "1.05rem",
                                            }}
                                        >
                                            {c.name}
                                        </Typography>
                                        {c.isDefault && (
                                            <Box
                                                sx={{
                                                    fontFamily: "'Montserrat', sans-serif",
                                                    fontSize: "0.65rem",
                                                    fontWeight: 700,
                                                    backgroundColor: "rgba(30,193,149,0.15)",
                                                    color: "#0f7a5b",
                                                    border: "1px solid rgba(30,193,149,0.3)",
                                                    borderRadius: "999px",
                                                    px: 1,
                                                    py: 0.2,
                                                    letterSpacing: "0.08em",
                                                    textTransform: "uppercase",
                                                }}
                                            >
                                                Default
                                            </Box>
                                        )}
                                    </Box>
                                    {c.description && (
                                        <Typography
                                            sx={{
                                                fontFamily: "'Montserrat', sans-serif",
                                                color: "#6b7280",
                                                fontSize: "0.85rem",
                                                mt: 0.5,
                                            }}
                                        >
                                            {c.description}
                                        </Typography>
                                    )}
                                </Box>
                                <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                                    <Tooltip
                                        title={c.isActive ? "Active" : "Paused"}
                                    >
                                        <Switch
                                            checked={c.isActive}
                                            onChange={() => toggleActive(c)}
                                            disabled={isSaving}
                                        />
                                    </Tooltip>
                                    <Tooltip title="Edit">
                                        <IconButton
                                            size="small"
                                            onClick={() => openEdit(c)}
                                        >
                                            <EditOutlined fontSize="small" />
                                        </IconButton>
                                    </Tooltip>
                                    <Tooltip title="Delete">
                                        <IconButton
                                            size="small"
                                            onClick={() => onDelete(c)}
                                            disabled={isSaving}
                                        >
                                            <DeleteOutline fontSize="small" />
                                        </IconButton>
                                    </Tooltip>
                                </Box>
                            </FlexBetween>

                            <Divider sx={{ my: 2 }} />

                            <Box
                                sx={{
                                    display: "flex",
                                    flexDirection: "column",
                                    gap: 1,
                                }}
                            >
                                {c.rules.map((r, i) => (
                                    <RuleSummary
                                        key={i}
                                        rule={r}
                                        templateName={
                                            templates.find((t) => t._id === r.templateId)
                                                ?.name || "(template removed)"
                                        }
                                    />
                                ))}
                                {c.rules.length === 0 && (
                                    <Typography
                                        sx={{
                                            fontFamily: "'Montserrat', sans-serif",
                                            color: "#9ca3af",
                                            fontSize: "0.85rem",
                                            fontStyle: "italic",
                                        }}
                                    >
                                        No rules configured — add one to start sending
                                        reminders.
                                    </Typography>
                                )}
                            </Box>
                        </CollectSectionCard>
                    ))
                )}
            </Box>

            {/* Editor dialog */}
            <Dialog
                open={dialogOpen}
                onClose={() => setDialogOpen(false)}
                fullWidth
                maxWidth="md"
            >
                <DialogTitle
                    sx={{ fontFamily: "'Montserrat', sans-serif", fontWeight: 700 }}
                >
                    {draft._id ? "Edit campaign" : "New campaign"}
                </DialogTitle>
                <DialogContent>
                    {saveError && (
                        <CollectInfoBanner tone="warn">
                            <span>{saveError}</span>
                        </CollectInfoBanner>
                    )}
                    <TextField
                        fullWidth
                        label="Name"
                        margin="normal"
                        value={draft.name}
                        onChange={(e) => setDraft({ ...draft, name: e.target.value })}
                    />
                    <TextField
                        fullWidth
                        label="Description"
                        margin="normal"
                        multiline
                        minRows={2}
                        value={draft.description || ""}
                        onChange={(e) =>
                            setDraft({ ...draft, description: e.target.value })
                        }
                    />
                    <Box sx={{ display: "flex", gap: 3, mt: 1, alignItems: "center" }}>
                        <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                            <Switch
                                checked={draft.isActive}
                                onChange={(e) =>
                                    setDraft({ ...draft, isActive: e.target.checked })
                                }
                            />
                            <Typography
                                sx={{
                                    fontFamily: "'Montserrat', sans-serif",
                                    fontSize: "0.85rem",
                                }}
                            >
                                Active
                            </Typography>
                        </Box>
                        <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                            <Switch
                                checked={draft.isDefault}
                                onChange={(e) =>
                                    setDraft({ ...draft, isDefault: e.target.checked })
                                }
                            />
                            <Typography
                                sx={{
                                    fontFamily: "'Montserrat', sans-serif",
                                    fontSize: "0.85rem",
                                }}
                            >
                                Set as default
                            </Typography>
                        </Box>
                    </Box>

                    <Divider sx={{ my: 2 }} />
                    <FlexBetween>
                        <Typography
                            sx={{
                                fontFamily: "'Montserrat', sans-serif",
                                fontWeight: 700,
                                color: colors.primary,
                            }}
                        >
                            Rules ({draft.rules.length})
                        </Typography>
                        <CollectGhostButton
                            startIcon={<Add fontSize="small" />}
                            onClick={addRule}
                            disabled={templates.length === 0}
                        >
                            Add rule
                        </CollectGhostButton>
                    </FlexBetween>

                    {templates.length === 0 && (
                        <CollectInfoBanner tone="warn">
                            <span>
                                You need at least one template before adding rules.
                                Create one in Templates.
                            </span>
                        </CollectInfoBanner>
                    )}

                    <Box
                        sx={{
                            display: "flex",
                            flexDirection: "column",
                            gap: 2,
                            mt: 2,
                        }}
                    >
                        {draft.rules.map((r, idx) => (
                            <Box
                                key={idx}
                                sx={{
                                    border: "1px solid rgba(15,23,42,0.08)",
                                    borderRadius: "10px",
                                    padding: "14px",
                                    backgroundColor: "#fafafa",
                                }}
                            >
                                <FlexBetween>
                                    <TextField
                                        size="small"
                                        label="Rule name"
                                        value={r.name}
                                        onChange={(e) =>
                                            updateRule(idx, { name: e.target.value })
                                        }
                                        sx={{ flex: 1, mr: 1 }}
                                    />
                                    <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                                        <Switch
                                            size="small"
                                            checked={r.isActive}
                                            onChange={(e) =>
                                                updateRule(idx, { isActive: e.target.checked })
                                            }
                                        />
                                        <IconButton
                                            size="small"
                                            onClick={() => removeRule(idx)}
                                        >
                                            <Remove fontSize="small" />
                                        </IconButton>
                                    </Box>
                                </FlexBetween>
                                <Box
                                    sx={{
                                        display: "grid",
                                        gridTemplateColumns: {
                                            xs: "1fr",
                                            sm: "1fr 1fr 1fr",
                                        },
                                        gap: 1.5,
                                        mt: 1.5,
                                    }}
                                >
                                    <TextField
                                        size="small"
                                        select
                                        label="When"
                                        value={r.trigger}
                                        onChange={(e) =>
                                            updateRule(idx, {
                                                trigger: e.target.value as DunningRuleTrigger,
                                            })
                                        }
                                    >
                                        <MenuItem value="before_due">Before due</MenuItem>
                                        <MenuItem value="due_today">On due date</MenuItem>
                                        <MenuItem value="after_due">After due</MenuItem>
                                    </TextField>
                                    <TextField
                                        size="small"
                                        type="number"
                                        label="Offset (days)"
                                        value={r.offsetDays}
                                        onChange={(e) =>
                                            updateRule(idx, {
                                                offsetDays: Number(e.target.value) || 0,
                                            })
                                        }
                                        disabled={r.trigger === "due_today"}
                                    />
                                    <TextField
                                        size="small"
                                        select
                                        label="Channel"
                                        value={r.channel}
                                        onChange={(e) =>
                                            updateRule(idx, {
                                                channel: e.target.value as DunningChannel,
                                            })
                                        }
                                    >
                                        <MenuItem value="email">Email</MenuItem>
                                        <MenuItem value="sms">SMS</MenuItem>
                                        <MenuItem value="both">Email + SMS</MenuItem>
                                    </TextField>
                                </Box>
                                <TextField
                                    size="small"
                                    select
                                    fullWidth
                                    label="Template"
                                    margin="normal"
                                    value={r.templateId}
                                    onChange={(e) =>
                                        updateRule(idx, { templateId: e.target.value })
                                    }
                                >
                                    {templates.map((t) => (
                                        <MenuItem key={t._id} value={t._id}>
                                            {t.name} ({t.channel.toLowerCase()})
                                        </MenuItem>
                                    ))}
                                </TextField>
                            </Box>
                        ))}
                    </Box>
                </DialogContent>
                <DialogActions>
                    <CollectGhostButton onClick={() => setDialogOpen(false)}>
                        Cancel
                    </CollectGhostButton>
                    <CollectPrimaryButton
                        onClick={onSave}
                        disabled={isSaving || !draft.name.trim()}
                        startIcon={
                            isSaving ? (
                                <CircularProgress size={14} sx={{ color: "#fff" }} />
                            ) : undefined
                        }
                    >
                        {isSaving ? "Saving…" : "Save campaign"}
                    </CollectPrimaryButton>
                </DialogActions>
            </Dialog>
        </CollectPageWrapper>
    );
};

const RuleSummary: React.FC<{ rule: IDunningRule; templateName: string }> = ({
    rule,
    templateName,
}) => {
    let timing = "";
    if (rule.trigger === "due_today") timing = "On due date";
    else if (rule.trigger === "before_due")
        timing = `${rule.offsetDays} ${TRIGGER_LABELS.before_due}`;
    else timing = `${rule.offsetDays} ${TRIGGER_LABELS.after_due}`;

    return (
        <Box
            sx={{
                display: "flex",
                gap: 1.5,
                padding: "10px 14px",
                border: "1px solid rgba(15,23,42,0.06)",
                borderRadius: "8px",
                backgroundColor: rule.isActive ? "#ffffff" : "#f3f4f6",
                opacity: rule.isActive ? 1 : 0.6,
                flexWrap: "wrap",
                alignItems: "center",
            }}
        >
            <Typography
                sx={{
                    fontFamily: "'Montserrat', sans-serif",
                    fontWeight: 700,
                    fontSize: "0.85rem",
                    color: colors.primary,
                    minWidth: 140,
                }}
            >
                {rule.name}
            </Typography>
            <Box sx={{ fontFamily: "'Montserrat', sans-serif", fontSize: "0.82rem", color: "#374151" }}>
                <strong>{timing}</strong> · via{" "}
                <strong>{CHANNEL_LABELS[rule.channel]}</strong> · template:{" "}
                <em>{templateName}</em>
            </Box>
        </Box>
    );
};

export default CollectCampaigns;
